import { UserRoundPlus } from "lucide-react";
import { Location } from "history";

import queryString from "query-string";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Redirect, useHistory, useParams } from "react-router-dom";

import { API_V2_ROOT } from "@/config";

import { Banner } from "@/design-system/components/Banner/Banner";

import { Button } from "@/design-system/components/Button/Button";
import { Card } from "@/design-system/components/Card/Card";
import { Divider } from "@/design-system/components/Divider";
import "./SignInScreen.css";
import { setLoginPage } from "./actions/loginPage";
import { CustomAuthUI } from "./components/auth/CustomAuthUI";
import useLoginPage from "./hooks/useLoginPage";
import { providerMap } from "@/auth/ssoProviderMap";
import { getCurrentUser } from "@/auth/firebase";
import { clearPreviewModeFlag } from "@/preview-mode";
import SessionTimeoutManager from "./sessionTimeout/SessionTimeoutManager";

interface LoginProps {
  isSignedIn: boolean;
  location: Location;
  ssoOnly?: boolean;
}

/**
 * Submits the OIDC login callback to our backend via a self-submitting POST
 * form. This completes the login step in the zitadel/oidc authorization code
 * flow — the backend /login/callback handler verifies the Firebase token,
 * marks the auth request as done, and redirects the browser through the
 * library's authorize callback to issue the code and redirect back to the
 * relying party (e.g. Bettermode).
 *
 * We use a form POST (rather than a GET redirect with query parameters) so
 * that the Firebase ID token is sent in the request body instead of the URL.
 * This prevents the token from leaking into browser history, Referer headers,
 * server access logs, and analytics pixels (e.g. GA4) that capture page URLs.
 */
async function redirectToOIDCLoginCallback(
  oidcRequestID: string,
): Promise<void> {
  const user = getCurrentUser();
  if (!user) {
    return;
  }

  const idToken = await user.getIdToken();

  // Build a hidden form that POSTs "id" and "id_token" to the backend's
  // /oidc/login/callback endpoint, then auto-submit it. The backend reads
  // both values from the form body so the token never appears in a URL.
  const form = document.createElement("form");
  form.method = "POST";
  form.action = `${API_V2_ROOT}/oidc/login/callback`;
  form.style.display = "none";

  const idField = document.createElement("input");
  idField.type = "hidden";
  idField.name = "id";
  idField.value = oidcRequestID;
  form.appendChild(idField);

  const tokenField = document.createElement("input");
  tokenField.type = "hidden";
  tokenField.name = "id_token";
  tokenField.value = idToken;
  form.appendChild(tokenField);

  document.body.appendChild(form);
  form.submit();
}

// Adds params the URL does not already carry, so a destination's own values
// win over ones from the sign-in page.
function withMissingParams(
  url: string,
  params: queryString.ParsedQuery,
): string {
  const { query } = queryString.parseUrl(url);
  const missing = Object.fromEntries(
    Object.entries(params).filter(([key]) => !(key in query)),
  );
  if (Object.keys(missing).length === 0) {
    return url;
  }
  return queryString.stringifyUrl({ url, query: missing }, { sort: false });
}

function Login(props: LoginProps) {
  const dispatch = useDispatch();
  const { providerName } = useParams<{ providerName?: string }>();
  const loginPage = useLoginPage();
  const history = useHistory();

  const [oidcRedirecting, setOidcRedirecting] = useState(false);

  // Clear preview mode whenever the sign-in screen is reachable. A session that
  // expired or was invalidated while preview mode was on must not bleed the
  // synthetic UI into the next authenticated session.
  useEffect(() => {
    clearPreviewModeFlag();
  }, []);

  const parsed = queryString.parse(props.location.search);
  // The zitadel/oidc library's client.LoginURL(id) redirects here with
  // oidc_request_id=<id>. After Firebase login completes we use this ID
  // to redirect to the backend's /oidc/login/callback.
  const oidcRequestID = parsed.oidc_request_id as string | undefined;
  const utmParams = Object.fromEntries(
    Object.entries(parsed).filter(([key]) => key.startsWith("utm_")),
  );
  const destination = withMissingParams(
    (parsed.next as string) || "/events",
    utmParams,
  );

  // When the user is signed in and an OIDC request ID is present, redirect
  // to the backend login callback with a fresh Firebase token.
  useEffect(() => {
    if (props.isSignedIn && oidcRequestID && !oidcRedirecting) {
      setOidcRedirecting(true);
      redirectToOIDCLoginCallback(oidcRequestID).catch((err) => {
        console.error("Failed to redirect to OIDC login callback", err);
        setOidcRedirecting(false);
      });
    }
  }, [props.isSignedIn, oidcRequestID, oidcRedirecting]);

  // While we're fetching the Firebase token and redirecting to the backend,
  // show a brief loading state instead of flashing the login form.
  if (oidcRedirecting) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-textSubtle text-sm">
          Redirecting to community&hellip;
        </p>
      </div>
    );
  }

  if (props.isSignedIn && !oidcRequestID) {
    return <Redirect to={destination} />;
  }

  // Moving between login pages forwards only the parameters the other page
  // needs to finish the same sign-in, plus UTM parameters. Firebase email-link
  // parameters (`apiKey`, `oobCode`, `mode`) are left behind so a one-time
  // code never completes sign-in on, or leaks through the URL of, a different
  // page.
  const loginPageSearch = queryString.stringify({
    next: parsed.next,
    oidc_request_id: parsed.oidc_request_id,
    ...utmParams,
  });

  // If the user logged in via SSO last time, redirect them to that SSO login page
  if (!props.isSignedIn && loginPage !== "/login" && !props.ssoOnly) {
    return <Redirect to={{ pathname: loginPage, search: loginPageSearch }} />;
  }

  const onClickReturnToMainSignInPage = () => {
    dispatch(setLoginPage("/login"));
    history.push({ pathname: "/login", search: loginPageSearch });
  };

  const shouldUseSSOProvider =
    props.ssoOnly && providerName && providerMap[providerName];

  const signInOptions = shouldUseSSOProvider
    ? [providerMap[providerName!]]
    : [
        "google.com",
        "github.com",
        {
          provider: "password",
          signInMethod: "emailLink",
        },
      ];

  const emailLinkConfig = {
    url: `${window.location.origin}/login?${loginPageSearch}`,
    handleCodeInApp: true,
  };

  return (
    <div className="flex min-h-screen">
      <div className="max-w-full grow overflow-x-hidden pt-16 max-sm:pt-14">
        <div className="flex h-full items-center justify-center px-4 py-12">
          <Card className="relative w-120 max-w-full gap-0 px-8 pt-8 pb-6">
            <div className="space-y-4 text-center">
              <div className="absolute -top-10 left-1/2 m-auto mb-4 flex size-16 -translate-x-1/2 items-center justify-center rounded bg-[#EE5757]">
                <UserRoundPlus className="size-9 text-white" />
              </div>
              <h3 className="my-2">Welcome to Freshpaint</h3>
              <p className="text-textSubtle text-sm">
                Unlock your data, ensure compliance, and drive marketing
                success.
              </p>
              <Divider />

              <CustomAuthUI
                key={providerName || ""}
                signInOptions={signInOptions}
                emailLinkConfig={emailLinkConfig}
              />
              <Divider />
              <p className="text-textSubtle text-sm">
                By continuing, you are indicating that you accept our{" "}
                <a href="https://www.freshpaint.io/terms">Terms of Service</a>.
              </p>
              {shouldUseSSOProvider && (
                <>
                  <br />
                  <Button
                    variant="outline"
                    onClick={onClickReturnToMainSignInPage}
                  >
                    Return to the main sign-in page
                  </Button>
                </>
              )}
              {SessionTimeoutManager.logoutMessage() !== "" && (
                <Banner type="warning">
                  <p className="text-sm">
                    {SessionTimeoutManager.logoutMessage()}
                  </p>
                </Banner>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Login;
